"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { ClientDetailsDialog } from "@/components/clients/client-details-dialog";
import { QuoteDetailsDialog } from "@/components/quotes/quote-details-dialog";
import { VehicleDetailsDialog } from "@/components/vehicles/vehicle-details-dialog";
import { WorkOrderDetailsDialog } from "@/components/work-orders/work-order-details-dialog";

type RelatedEntityType = "client" | "vehicle" | "workOrder" | "quote";

type RelatedEntity = {
  type: RelatedEntityType;
  id: string;
};

type RelatedEntityNavigationValue = {
  openRelatedEntity: (entity: RelatedEntity) => void;
  closeRelatedEntity: () => void;
  goBackRelatedEntity: () => void;
};

const RelatedEntityNavigationContext = createContext<RelatedEntityNavigationValue | null>(null);

export function RelatedEntityNavigationProvider({ children }: { children: ReactNode }) {
  const [history, setHistory] = useState<RelatedEntity[]>([]);
  const activeEntity = history.at(-1) ?? null;

  const openRelatedEntity = useCallback((entity: RelatedEntity) => {
    setHistory((current) => [...current, entity]);
  }, []);

  const closeRelatedEntity = useCallback(() => {
    setHistory([]);
  }, []);

  const goBackRelatedEntity = useCallback(() => {
    setHistory((current) => current.slice(0, -1));
  }, []);

  const value = useMemo(
    () => ({ openRelatedEntity, closeRelatedEntity, goBackRelatedEntity }),
    [closeRelatedEntity, goBackRelatedEntity, openRelatedEntity],
  );

  const openClient = (id: string) => openRelatedEntity({ type: "client", id });
  const openVehicle = (id: string) => openRelatedEntity({ type: "vehicle", id });
  const openWorkOrder = (id: string) => openRelatedEntity({ type: "workOrder", id });

  return (
    <RelatedEntityNavigationContext.Provider value={value}>
      {children}
      <ClientDetailsDialog
        key={activeEntity?.type === "client" ? `client:${activeEntity.id}` : "client:closed"}
        open={activeEntity?.type === "client"}
        clientId={activeEntity?.type === "client" ? activeEntity.id : null}
        onClose={closeRelatedEntity}
        onBack={history.length > 1 ? goBackRelatedEntity : undefined}
      />
      <VehicleDetailsDialog
        open={activeEntity?.type === "vehicle"}
        vehicleId={activeEntity?.type === "vehicle" ? activeEntity.id : null}
        onClose={closeRelatedEntity}
        onBack={history.length > 1 ? goBackRelatedEntity : undefined}
        onOpenClient={openClient}
        onOpenWorkOrder={openWorkOrder}
      />
      <WorkOrderDetailsDialog
        open={activeEntity?.type === "workOrder"}
        workOrderId={activeEntity?.type === "workOrder" ? activeEntity.id : null}
        onClose={closeRelatedEntity}
        onBack={history.length > 1 ? goBackRelatedEntity : undefined}
        onOpenClient={openClient}
        onOpenVehicle={openVehicle}
      />
      <QuoteDetailsDialog
        open={activeEntity?.type === "quote"}
        quoteId={activeEntity?.type === "quote" ? activeEntity.id : null}
        onClose={closeRelatedEntity}
        onBack={history.length > 1 ? goBackRelatedEntity : undefined}
        onOpenClient={openClient}
        onOpenVehicle={openVehicle}
      />
    </RelatedEntityNavigationContext.Provider>
  );
}

export function useRelatedEntityNavigation() {
  const value = useContext(RelatedEntityNavigationContext);
  if (!value) {
    throw new Error("useRelatedEntityNavigation deve ser usado dentro de RelatedEntityNavigationProvider.");
  }
  return value;
}
